// Shared action menus for list rows and mobile cards.
//
// Pages pass a flat config array; the presentation comes from `@smartsash/ui`. HS-1178 replaced
// the hand-rolled scaffolding — `getBoundingClientRect` + `createPortal` + a `menuPos` state —
// with the package's `DropdownMenu`, which portals itself, keeps clear of the viewport edges,
// and closes on outside click or Escape. What stays local is the `RowActionEntry[]` format that
// 14 files depend on.

import { usePermission, type ActionId } from '@smartsash/permissions';
import { DropdownMenu, Icon, MenuItem, MenuSeparator, type IconName } from '@smartsash/ui';
import { useMemo, useRef, useState } from 'react';

import styles from './RowActions.module.css';

export interface RowActionItem {
  label: string;
  icon: IconName;
  onClick: () => void;
  /**
   * Hide this entry unless the session may perform the action. Omit for an entry every viewer of
   * the row may use — navigating to a related record, say, whose own route is guarded anyway.
   *
   * A UI action, not a backend permission key: the key an action needs lives in
   * `ACTION_PERMISSIONS`, so a rename or a split on the backend lands there rather than in the
   * fourteen files that build these configs.
   */
  permission?: ActionId;
  /** Shown but not selectable — e.g. while the row's previous action is still pending. */
  disabled?: boolean;
}

// `null` items render as separators — keeps configs flat.
export type RowActionEntry = RowActionItem | null;

interface RowActionsProps {
  items: RowActionEntry[];
  ariaLabel?: string;
}

/**
 * Drop the entries the session may not use, then tidy up after them.
 *
 * Filtering alone is not enough: the configs are flat arrays where `null` is a separator, so
 * removing an item can leave a leading, trailing or doubled rule — a menu that looks like it lost
 * something. Collapsing them keeps the menu looking authored rather than filtered. An empty result
 * means the trigger itself should not render, which the callers below handle: a "more options"
 * button that opens an empty popup is worse than no button.
 */
function useVisibleEntries(items: RowActionEntry[]): RowActionEntry[] {
  const { can } = usePermission();

  return useMemo(() => {
    const allowed = items.filter((item) => item?.permission == null || can(item.permission));

    const tidied: RowActionEntry[] = [];
    for (const entry of allowed) {
      if (entry === null && (tidied.length === 0 || tidied.at(-1) === null)) continue;
      tidied.push(entry);
    }
    while (tidied.at(-1) === null) tidied.pop();

    return tidied;
  }, [items, can]);
}

function renderItems(items: RowActionEntry[], close: () => void) {
  return items.map((item, idx) => {
    if (item === null) return <MenuSeparator key={`sep-${String(idx)}`} />;
    return (
      // `role` is explicit: `MenuItem` defaults to `option`, which belongs to a listbox. An
      // action menu that kept the default would present a wrong accessibility tree.
      <MenuItem
        key={item.label}
        role="menuitem"
        size="sm"
        leading={<Icon name={item.icon} />}
        disabled={item.disabled}
        onSelect={() => {
          close();
          item.onClick();
        }}
      >
        {item.label}
      </MenuItem>
    );
  });
}

// Table-cell variant: an icon-only "more" trigger.
export function RowActions({ items, ariaLabel = 'More options' }: RowActionsProps) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const visible = useVisibleEntries(items);

  if (visible.length === 0) return null;

  return (
    <div className={styles['action-wrapper']}>
      <button
        ref={btnRef}
        type="button"
        className={styles['action-btn']}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <Icon name="dots-vertical" size={20} color="var(--text-quaternary)" />
      </button>
      <DropdownMenu
        anchorRef={btnRef}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        role="menu"
        placement="bottom-end"
        matchWidth={false}
      >
        {renderItems(visible, () => {
          setOpen(false);
        })}
      </DropdownMenu>
    </div>
  );
}

interface MobileCardActionsProps {
  items: RowActionEntry[];
  label?: string;
}

// Mobile-card variant: a full-width labelled trigger. On small screens the package presents the
// menu as a bottom sheet rather than an anchored panel — a deliberate UX change (HS-1178).
export function MobileCardActions({ items, label = 'Actions' }: MobileCardActionsProps) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const visible = useVisibleEntries(items);

  if (visible.length === 0) return null;

  return (
    <div className={styles['m-card-action-wrap']}>
      <button
        ref={btnRef}
        type="button"
        className={styles['m-card-action']}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        {label}
      </button>
      <DropdownMenu
        anchorRef={btnRef}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        role="menu"
      >
        {renderItems(visible, () => {
          setOpen(false);
        })}
      </DropdownMenu>
    </div>
  );
}

export default RowActions;
